import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { apiClient } from './services/api';
import TopBar from './components/navigation/TopBar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DesktopShell from './layouts/DesktopShell';
import Video from './pages/Video';
import Image from './pages/Image';
import MyVideos from './pages/MyVideos';
import PricingPage from './components/PricingPage';
import HomePage from './pages/HomePage';
import ImageEditingPage from './pages/ImageEditingPage';
import TemplateToVideoPage from './pages/TemplateToVideoPage';
import TemplatesPage from './pages/TemplatesPage';
import UpgradePage from './pages/UpgradePage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import CheckoutEmbeddedPage from './pages/CheckoutEmbeddedPage';
import CheckoutReturnPage from './pages/CheckoutReturnPage';
import CreditsPage from './pages/CreditsPage';
import CreditsSuccessPage from './pages/CreditsSuccessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import GuidesIndexPage from './pages/GuidesIndexPage';
import GuidePage from './pages/GuidePage';
import LoginFlowModal from './components/auth/LoginFlowModal';
import RequireAuthGate from './components/guards/RequireAuthGate';
import ErrorBoundary from './components/ErrorBoundary';
import { updateLastNonLoginPath } from './utils/authNavigation';
import { useTracking } from './hooks/useTracking';
import { getSubscriptionPlans } from './services/credits';
import { getUserAssets } from './services/assets';
import { usePostHog } from 'posthog-js/react';

// Lazy load Lander1Page for code splitting - high-traffic landing page
const Lander1Page = lazy(() => import('./pages/Lander1Page'));
const Lander1Skeleton = lazy(() => import('./components/skeletons/Lander1Skeleton'));

function AppContent() {
  const { loading: isAuthLoading, getAccessToken, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useTracking();
  const posthog = usePostHog();

  // Set up API client to use Supabase tokens
  useEffect(() => {
    apiClient.setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Prefetch commonly accessed data on app load to warm cache
  useEffect(() => {
    // Prefetch subscription plans (used on /upgrade page)
    getSubscriptionPlans().catch(() => {
      // Silent fail - cache warming is best-effort
    });

    // If user is authenticated, prefetch their videos
    if (isAuthenticated) {
      getUserAssets('video').catch(() => {
        // Silent fail - cache warming is best-effort
      });
    }
  }, [isAuthenticated]);

  // Force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // CRITICAL: Intercept OAuth hash on homepage BEFORE page renders
  // This must run BEFORE other effects to prevent homepage flash
  useEffect(() => {
    // Only intercept on homepage
    if (location.pathname !== '/') return;
    
    const hash = window.location.hash;
    if (hash.includes('access_token=') || hash.includes('error=')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const error = hashParams.get('error');
      const errorCode = hashParams.get('error_code');
      
      // Get intended redirect path with localStorage fallback
      let intendedPath = sessionStorage.getItem('oauth_return_path');
      
      // Fallback to localStorage if sessionStorage was cleared
      if (!intendedPath) {
        const backupPath = localStorage.getItem('oauth_return_path_backup');
        const backupTimestamp = localStorage.getItem('oauth_return_path_timestamp');
        
        // Only use backup if it's recent (< 5 minutes)
        if (backupPath && backupTimestamp) {
          const age = Date.now() - parseInt(backupTimestamp);
          if (age < 5 * 60 * 1000) {
            intendedPath = backupPath;
            console.log('[App] Using localStorage backup for return path:', intendedPath);
          }
        }
      }
      
      // Final fallback
      intendedPath = intendedPath || '/lander-1';
      
      if (error) {
        // Handle OAuth error - clean hash for errors
        console.warn('[App] OAuth error detected on homepage:', error, errorCode);
        window.history.replaceState(null, '', '/');
        navigate('/login?error=' + encodeURIComponent(error), { replace: true });
        return;
      }
      
      if (accessToken) {
        console.log('[App] OAuth hash detected on homepage, redirecting immediately to:', intendedPath);
        
        // DON'T clean hash yet - let Supabase process it first via onAuthStateChange
        // The hash will be preserved during navigation and cleaned up later by AuthContext
        // This ensures Supabase can read the tokens before they're removed
        navigate(intendedPath, { replace: true });
        
        posthog.capture('oauth_homepage_intercepted', {
          intended_path: intendedPath,
          used_backup: !sessionStorage.getItem('oauth_return_path'),
        });
        
        // Clean up localStorage backup after successful use
        localStorage.removeItem('oauth_return_path_backup');
        localStorage.removeItem('oauth_return_path_timestamp');
      }
    }
  }, [location.pathname, navigate, posthog]);

  // Track last non-login path in sessionStorage
  useEffect(() => {
    updateLastNonLoginPath(location.pathname);
  }, [location.pathname]);

  // Save current path when page becomes hidden (mobile browser backgrounding)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - save current path (except auth/login pages)
        const currentPath = location.pathname;
        if (currentPath !== '/auth/callback' && currentPath !== '/login') {
          sessionStorage.setItem('ga:lastPath', currentPath);
          // Also save timestamp to detect stale state
          sessionStorage.setItem('ga:lastPathTimestamp', Date.now().toString());
        }
      } else {
        // Page visible - restore path if we're on wrong page
        const savedPath = sessionStorage.getItem('ga:lastPath');
        const savedTimestamp = sessionStorage.getItem('ga:lastPathTimestamp');
        
        if (savedPath && savedTimestamp) {
          const age = Date.now() - parseInt(savedTimestamp);
          const maxAge = 30 * 60 * 1000; // 30 minutes (for Meta ad users who may be distracted)
          
          // Only restore if:
          // 1. Saved path is different from current
          // 2. Not on auth/login pages
          // 3. Saved state is recent (< 30 minutes)
          // 4. Not on /lander-1 (let Lander1Page handle its own restoration)
          if (age < maxAge && 
              savedPath !== location.pathname &&
              location.pathname !== '/auth/callback' &&
              location.pathname !== '/login' &&
              location.pathname !== '/lander-1') {
            
            console.log('[App] Restoring navigation state:', savedPath);
            navigate(savedPath, { replace: true });
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname, navigate]);

  // Track pageviews - global deduplication in useTracking will prevent duplicates
  // Use ref to avoid dependency on track function
  const trackRef = useRef(track);
  trackRef.current = track;
  
  useEffect(() => {
    // Skip tracking for auth callback page (handled separately)
    if (location.pathname !== '/auth/callback') {
      trackRef.current('pageview', {
        page_path: location.pathname,
        page_title: document.title,
        page_url: window.location.href,
      });
    }
  }, [location.pathname]); // Remove track from dependencies

  // CRITICAL: Block stale OAuth callbacks from disrupting user flow (REVENUE PROTECTION)
  // This prevents delayed OAuth redirects from pulling users away from critical pages like /upgrade
  useEffect(() => {
    const urlHasAccessToken = window.location.hash.includes('access_token=');
    const isCallbackUrl = window.location.pathname === '/auth/callback' || 
                          window.location.search.includes('oauth_flow=');
    
    // Check if OAuth was initiated recently (within 30 seconds)
    const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
    const isRecentOAuth = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 30000; // 30 seconds
    
    // If this looks like an OAuth callback AND (user is already authenticated OR OAuth is stale)
    if ((urlHasAccessToken || isCallbackUrl) && !isAuthLoading) {
      const currentPath = location.pathname;
      const isCriticalPage = currentPath === '/upgrade' || 
                            currentPath === '/credits' || 
                            currentPath.includes('checkout') ||
                            currentPath === '/lander-1';
      
      // Block if: user already authenticated OR OAuth is stale (older than 30s)
      const shouldBlock = (isAuthenticated && !isRecentOAuth) || (!isAuthenticated && !isRecentOAuth);
      
      if (shouldBlock) {
        const oauthAge = oauthInitiatedTime ? Date.now() - parseInt(oauthInitiatedTime) : null;
        
        console.warn('[App] BLOCKED stale OAuth callback', {
          currentPath,
          isCriticalPage,
          hasAccessToken: urlHasAccessToken,
          isCallbackUrl,
          isAuthenticated,
          isRecentOAuth,
          oauthAge: oauthAge !== null ? oauthAge : 'N/A',
        });
        
        // Remove OAuth tokens from URL without navigating
        if (urlHasAccessToken || window.location.search.includes('oauth_flow=')) {
          // Clean URL hash and query params without navigation
          const cleanUrl = window.location.origin + currentPath;
          window.history.replaceState(null, '', cleanUrl);
          
          // Aggressively clean up ALL OAuth flags
          sessionStorage.removeItem('genanything_oauth_initiated');
          sessionStorage.removeItem('genanything_oauth_provider');
          sessionStorage.removeItem('oauth_initiated_time');
          sessionStorage.removeItem('oauth_provider');
          
          // Track revenue-protection event with detailed debugging info
          posthog.capture('blocked_stale_oauth_callback', {
            blocking_layer: 'app_tsx',
            from_page: currentPath,
            is_critical_page: isCriticalPage,
            reason: isAuthenticated ? 'user_already_authenticated' : 'oauth_too_old',
            oauth_age_ms: oauthAge,
            had_access_token: urlHasAccessToken,
            was_callback_url: isCallbackUrl,
          });
        }
        
        // If we're on /auth/callback, redirect away immediately
        if (currentPath === '/auth/callback') {
          const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
          console.warn('[App] Redirecting away from stale callback to:', savedPath);
          navigate(savedPath, { replace: true });
        }
        
        // NEVER navigate to /auth/callback for stale OAuth
        return;
      }
    }
  }, [location, isAuthenticated, isAuthLoading, posthog, navigate]);

  // CRITICAL: Redirect already-authenticated users away from callback page
  // BUT: Only if this is NOT a legitimate OAuth callback
  // Prevents random OAuth redirects from breaking checkout flow
  const isAuthCallbackPage = location.pathname === '/auth/callback';
  useEffect(() => {
    if (isAuthCallbackPage && isAuthenticated && !isAuthLoading) {
      // Check if this is a legitimate OAuth callback
      const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
      const isRecentOAuth = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 30000; // 30 seconds
      
      // Check for redirect_to parameter (indicates OAuth flow)
      const urlParams = new URLSearchParams(window.location.search);
      const hasRedirectTo = urlParams.has('redirect_to');
      const oauthFlow = urlParams.get('oauth_flow') === 'true';
      
      // If this is a legitimate OAuth callback, let AuthCallbackPage handle it
      if (isRecentOAuth || hasRedirectTo || oauthFlow) {
        console.log('[App] Legitimate OAuth callback detected, letting AuthCallbackPage process it');
        return; // Don't redirect - let AuthCallbackPage handle it
      }
      
      // Only redirect if this is truly a stale callback
      console.warn('[App] Stale callback detected - user already authenticated on /auth/callback');
      
      // Check if user came from a critical page
      const referrer = document.referrer;
      const wasOnCriticalPage = referrer.includes('/upgrade') || 
                                referrer.includes('/credits') ||
                                referrer.includes('checkout');
      
      if (wasOnCriticalPage) {
        // Extract the critical page path from referrer
        try {
          const url = new URL(referrer);
          const criticalPath = url.pathname;
          
          console.warn('[App] Redirecting back to critical page:', criticalPath);
          navigate(criticalPath, { replace: true });
          
          posthog.capture('recovered_from_stale_callback', {
            original_page: criticalPath,
            is_critical: true,
          });
        } catch (e) {
          // If referrer parsing fails, go home
          navigate('/', { replace: true });
        }
      } else {
        console.warn('[App] Redirecting stale callback to home');
        navigate('/', { replace: true });
      }
    }
  }, [isAuthCallbackPage, isAuthenticated, isAuthLoading, navigate, posthog, location.search]);

  if (isAuthLoading) {
    return (
      <div className="h-screen bg-bg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Auth callback page doesn't need TopBar/Footer
  // Note: isAuthCallbackPage is already defined above with the useEffect hook

  if (isAuthCallbackPage) {
    return (
      <div className="h-screen bg-bg">
        <ScrollToTop />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
        </Routes>
      </div>
    );
  }

  // Check if we're on the lander-1 page (hide all navigation)
  const isLander1 = location.pathname === '/lander-1';
  
  // Check if we're on the upgrade page (hide all navigation)
  const isUpgradePage = location.pathname === '/upgrade';

  // Special layout for lander-1: no navigation, full page
  if (isLander1) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        {/* Global auth modal - rendered once at app root */}
        <LoginFlowModal />
        <Suspense fallback={<Lander1Skeleton />}>
          <Routes>
            <Route path="/lander-1" element={<Lander1Page />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Special layout for upgrade page: no navigation, full page
  if (isUpgradePage) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        {/* Global auth modal - rendered once at app root */}
        <LoginFlowModal />
        <Suspense fallback={
          <div className="min-h-screen bg-bg flex items-center justify-center">
            <div className="animate-pulse text-white/60">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/upgrade" element={<ErrorBoundary><UpgradePage /></ErrorBoundary>} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="h-screen bg-bg dark:bg-bg flex flex-col overflow-x-hidden">
      <ScrollToTop />
      {/* Global auth modal - rendered once at app root */}
      <LoginFlowModal />
      <TopBar />
      
      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col h-full">
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Suspense fallback={
            <div className="min-h-screen bg-bg flex items-center justify-center">
              <div className="animate-pulse text-white/60">Loading...</div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/video/:mode" element={<Video />} />
              <Route path="/image/:mode" element={<Image />} />
              <Route path="/image-to-video" element={<Navigate to="/" replace />} />
              <Route path="/image-editing" element={<ImageEditingPage />} />
              <Route path="/template-to-video" element={<TemplateToVideoPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/lander-1" element={<Lander1Page />} />
              <Route path="/upgrade" element={<ErrorBoundary><UpgradePage /></ErrorBoundary>} />
              <Route path="/my-collection" element={<RequireAuthGate><MyVideos /></RequireAuthGate>} />
              <Route path="/assets" element={<Navigate to="/my-collection" replace />} />
              <Route path="/billing" element={<PricingPage />} />
              <Route path="/credits" element={<RequireAuthGate><CreditsPage /></RequireAuthGate>} />
              <Route path="/credits/success" element={<RequireAuthGate><CreditsSuccessPage /></RequireAuthGate>} />
              <Route path="/checkout" element={<CheckoutEmbeddedPage />} />
              <Route path="/return" element={<CheckoutReturnPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/guides" element={<GuidesIndexPage />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
            </Routes>
          </Suspense>
          <Footer />
        </main>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-col h-full">
        <DesktopShell>
          <Suspense fallback={
            <div className="min-h-screen bg-bg flex items-center justify-center">
              <div className="animate-pulse text-white/60">Loading...</div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/video/:mode" element={<Video />} />
              <Route path="/image/:mode" element={<Image />} />
              <Route path="/image-to-video" element={<Navigate to="/" replace />} />
              <Route path="/image-editing" element={<ImageEditingPage />} />
              <Route path="/template-to-video" element={<TemplateToVideoPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/lander-1" element={<Lander1Page />} />
            <Route path="/upgrade" element={<ErrorBoundary><UpgradePage /></ErrorBoundary>} />
            <Route path="/my-collection" element={<RequireAuthGate><MyVideos /></RequireAuthGate>} />
            <Route path="/assets" element={<Navigate to="/my-collection" replace />} />
            <Route path="/billing" element={<PricingPage />} />
            <Route path="/credits" element={<RequireAuthGate><CreditsPage /></RequireAuthGate>} />
            <Route path="/credits/success" element={<RequireAuthGate><CreditsSuccessPage /></RequireAuthGate>} />
            <Route path="/checkout" element={<CheckoutEmbeddedPage />} />
            <Route path="/return" element={<CheckoutReturnPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/guides" element={<GuidesIndexPage />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
          </Routes>
          </Suspense>
        </DesktopShell>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthModalProvider>
      <AppContent />
    </AuthModalProvider>
  );
}

export default App;

